// Small Talmia 2.0 dashboard-style widgets used by the Smart Call screen.
// Deliberately dependency-free so the same tokens can be reused elsewhere.

/** Circular score ring (0-100). */
export function ScoreRing({ value = 0, label = "Score", size = 108, testid }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (v / 100);
  const tone = v >= 80 ? "var(--t2-ok)" : v >= 60 ? "var(--t2-teal)" : v >= 40 ? "var(--t2-warn)" : "var(--t2-red)";
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 6 }} data-testid={testid}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--t2-surface3)" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r}
          fill="none" stroke={tone} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`} transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dasharray .5s ease" }}
        />
        <text x="50%" y="50%" textAnchor="middle" dy="0.05em"
          style={{ fill: "var(--t2-white)", fontFamily: "var(--t2-mono)", fontSize: size*0.30, fontWeight: 800 }}>
          {v}
        </text>
        <text x="50%" y="50%" textAnchor="middle" dy="1.5em"
          style={{ fill: "var(--t2-grey3)", fontFamily: "var(--t2-mono)", fontSize: size*0.10, letterSpacing: ".14em" }}>
          / 100
        </text>
      </svg>
      <div className="t2-mono" style={{
        fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
        color: "var(--t2-brass)", fontWeight: 700,
      }}>{label}</div>
    </div>
  );
}

/** Horizontal meter bar 0-max */
export function MeterBar({ label, value = 0, max = 100, tone = "orange", suffix = "", testid }) {
  const v = Math.max(0, Math.min(max, Number(value) || 0));
  const pct = max > 0 ? (v / max) * 100 : 0;
  return (
    <div data-testid={testid}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span className="t2-mono" style={{ fontSize: 9.5, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--t2-grey3)", fontWeight: 700 }}>{label}</span>
        <span className="t2-mono" style={{ fontSize: 12, color: "var(--t2-white)", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
          {v}{suffix}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--t2-surface3)", overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: `var(--t2-${tone})`,
          transition: "width .4s ease",
          borderRadius: 3,
        }} />
      </div>
    </div>
  );
}

/** Enum meter — draws three segments and highlights the current one. */
export function EnumMeter({ label, value, options = [], toneMap = {}, testid }) {
  return (
    <div data-testid={testid}>
      <div className="t2-mono" style={{
        fontSize: 9.5, letterSpacing: ".12em", textTransform: "uppercase",
        color: "var(--t2-grey3)", fontWeight: 700, marginBottom: 5,
      }}>{label}</div>
      <div style={{ display: "flex", gap: 4 }}>
        {options.map((o) => {
          const on = String(o).toLowerCase() === String(value || "").toLowerCase();
          const tone = toneMap[o] || (on ? "orange" : "");
          return (
            <div key={o} style={{
              flex: 1, height: 22, borderRadius: 5,
              background: on ? `var(--t2-${tone})` : "var(--t2-surface3)",
              border: `1px solid var(--t2-border${on ? "2" : ""})`,
              color: on ? "#0b1120" : "var(--t2-grey3)",
              fontFamily: "var(--t2-mono)",
              fontSize: 10,
              fontWeight: 700,
              display: "grid", placeItems: "center",
              letterSpacing: ".04em",
              transition: "background .2s ease",
            }}>{o}</div>
          );
        })}
      </div>
    </div>
  );
}
